import { useCallback, useState } from 'react';

export interface UseWizardOptions<P> {
  /** Last step id (the review / submit step). */
  total: number;
  /** First step id; the goods receipt form starts at 2 (step 1 is the pick list). */
  first?: number;
  /**
   * Problems that block moving forward from `step` (empty = may move). The
   * server stays the source of truth; this only guards required input.
   */
  check?: (step: number) => P[];
  /** Called after every step change (e.g. scroll the body to the top). */
  onMove?: (step: number) => void;
}

export interface Wizard<P> {
  step: number;
  isFirst: boolean;
  isLast: boolean;
  /** Guard the current step; moves forward when clean. Returns its problems. */
  next: () => P[];
  /** One step back; false when already on the first step (caller decides). */
  back: () => boolean;
  /** Go to a step (clamped to first…total). */
  goTo: (step: number) => void;
  /**
   * Guard every step before the last (submit). When any has problems the
   * wizard moves to the earliest of them. Returns every problem.
   */
  checkAll: () => P[];
  /** Move to the earliest of the given steps (no-op when empty). */
  jumpToEarliest: (steps: number[]) => void;
  /** Back to the first step (form reopened). */
  reset: () => void;
}

/**
 * Step state for the WMS multi-step forms (outbound request, purchase order,
 * goods receipt): next/back with a per-step guard and a jump to the earliest
 * step that has an error (local guard on submit, or a server 422).
 */
export function useWizard<P = unknown>({
  total,
  first = 1,
  check,
  onMove,
}: UseWizardOptions<P>): Wizard<P> {
  const [step, setStep] = useState(first);

  const clamp = useCallback(
    (s: number) => Math.max(first, Math.min(total, s)),
    [first, total]
  );

  const goTo = useCallback(
    (s: number) => {
      const target = clamp(s);
      setStep(target);
      onMove?.(target);
    },
    [clamp, onMove]
  );

  const next = (): P[] => {
    const problems = check ? check(step) : [];
    if (!problems.length) goTo(step + 1);
    return problems;
  };

  const back = (): boolean => {
    if (step <= first) return false;
    goTo(step - 1);
    return true;
  };

  const jumpToEarliest = (steps: number[]) => {
    if (steps.length) goTo(Math.min(...steps));
  };

  const checkAll = (): P[] => {
    const all: P[] = [];
    const failing: number[] = [];
    for (let s = first; s < total; s += 1) {
      const problems = check ? check(s) : [];
      if (problems.length) failing.push(s);
      all.push(...problems);
    }
    jumpToEarliest(failing);
    return all;
  };

  const reset = useCallback(() => setStep(first), [first]);

  return {
    step,
    isFirst: step <= first,
    isLast: step >= total,
    next,
    back,
    goTo,
    checkAll,
    jumpToEarliest,
    reset,
  };
}
